import { BaseInput, Button, ButtonVariant, Show } from "shared/components";
import { tenDigitTelParser } from "shared/components/forms/BaseInput/parsers";
import { CombinedIncomeLayout } from "../components/Layout";
import { COMBINED_INCOME_FORM_ID } from "../constants";
import { toast } from "react-toastify";
import { CombinedIncomeSubStep } from "../../../constants";
import { useFormTyped } from "shared/hooks/use-form-typed";
import { object, string } from "yup";
import { useCombinedIncome } from "../context";
import { getErrorMessage } from "utils/error";

type SupervisorDetailsFormData = {
  first_name: string;
  last_name: string;
  contact_email: string;
  contact_phone: string;
};

const createValidationSchema = (isFieldRequired: (path: string[]) => boolean) =>
  object({
    first_name: isFieldRequired(["supervisor", "first_name"])
      ? string().required("Supervisor first name is required")
      : string(),
    last_name: isFieldRequired(["supervisor", "last_name"])
      ? string().required("Supervisor last name is required")
      : string(),
    contact_email: isFieldRequired(["supervisor", "email"])
      ? string()
          .email("Please enter a valid email")
          .required("Supervisor email is required")
      : string().email("Please enter a valid email"),
    contact_phone: isFieldRequired(["supervisor", "phone"])
      ? string().required("Supervisor phone is required")
      : string()
  });

export const SupervisorDetailsStep = () => {
  const {
    updateStepMutation,
    stepData,
    getNextStep,
    refetchVerification,
    isFieldRequired,
    isFieldVisible
  } = useCombinedIncome();

  const { register, onSubmit } = useFormTyped<SupervisorDetailsFormData>(
    {
      first_name: stepData.supervisor?.first_name || "",
      last_name: stepData.supervisor?.last_name || "",
      contact_email: stepData.supervisor?.contact_email || "",
      contact_phone: stepData.supervisor?.contact_phone || ""
    },
    createValidationSchema(isFieldRequired)
  );

  const handleSubmit = (formValues: SupervisorDetailsFormData) => {
    const nextStep = getNextStep(CombinedIncomeSubStep.CompanyDetails, {
      incomeType: stepData.currentIncomeType
    });
    if (!nextStep) return;

    updateStepMutation.mutate(
      {
        lastStep: CombinedIncomeSubStep.PreTaxIncome,
        supervisor: {
          first_name: formValues.first_name,
          last_name: formValues.last_name,
          contact_email: formValues.contact_email,
          contact_phone: formValues.contact_phone
        }
      },
      {
        onSuccess: () => {
          refetchVerification();
        },
        onError: (error) => {
          toast.error(
            getErrorMessage(error) || "Failed to save supervisor details"
          );
        }
      }
    );
  };

  return (
    <form
      id={COMBINED_INCOME_FORM_ID}
      onSubmit={onSubmit({ onSuccess: handleSubmit })}
    >
      <CombinedIncomeLayout
        title="Add supervisor details"
        footer={
          <Button
            className="w-[125px]"
            type="submit"
            form={COMBINED_INCOME_FORM_ID}
            loading={updateStepMutation.isPending}
            variant={ButtonVariant.PRIMARY}
          >
            Continue
          </Button>
        }
      >
        <Show if={isFieldVisible(["supervisor", "first_name"])}>
          <BaseInput
            {...register("first_name")}
            id="supervisor-first-name"
            label="Supervisor first name"
            placeholder="John"
            labelClassName="mt-4"
            required={isFieldRequired(["supervisor", "first_name"])}
          />
        </Show>

        <Show if={isFieldVisible(["supervisor", "last_name"])}>
          <BaseInput
            {...register("last_name")}
            id="supervisor-last-name"
            label="Supervisor last name"
            placeholder="Doe"
            labelClassName="mt-4"
            required={isFieldRequired(["supervisor", "last_name"])}
          />
        </Show>

        <Show if={isFieldVisible(["supervisor", "email"])}>
          <BaseInput
            {...register("contact_email")}
            id="supervisor-email"
            label="Supervisor email"
            type="email"
            placeholder="john.doe@example.com"
            labelClassName="mt-4"
            required={isFieldRequired(["supervisor", "email"])}
          />
        </Show>

        <Show if={isFieldVisible(["supervisor", "phone"])}>
          <BaseInput
            {...register("contact_phone")}
            id="supervisor-phone"
            label="Supervisor phone"
            parser={tenDigitTelParser({ asString: true })}
            placeholder="(123) 000-0000"
            labelClassName="mt-4"
            required={isFieldRequired(["supervisor", "phone"])}
          />
        </Show>
      </CombinedIncomeLayout>
    </form>
  );
};
